import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { LayoutDashboard, Users, FileText, Settings, LogOut, Search, Download, GraduationCap, ChevronRight, MoreVertical } from 'lucide-react';
// Mock Data
const applications = [{
  id: 1,
  name: 'John Doe',
  email: 'john@example.com',
  country: 'UK',
  course: 'MSc Data Science',
  status: 'Pending',
  date: '2023-10-15'
}, {
  id: 2,
  name: 'Sarah Smith',
  email: 'sarah@example.com',
  country: 'Canada',
  course: 'MBA',
  status: 'Reviewing',
  date: '2023-10-14'
}, {
  id: 3,
  name: 'Michael Brown',
  email: 'michael@example.com',
  country: 'USA',
  course: 'BSc CS',
  status: 'Approved',
  date: '2023-10-12'
}, {
  id: 4,
  name: 'Emily Davis',
  email: 'emily@example.com',
  country: 'Australia',
  course: 'BCom',
  status: 'Rejected',
  date: '2023-10-10'
}, {
  id: 5,
  name: 'David Wilson',
  email: 'david@example.com',
  country: 'Germany',
  course: 'MSc Eng',
  status: 'Pending',
  date: '2023-10-09'
}, {
  id: 6,
  name: 'Lisa Anderson',
  email: 'lisa@example.com',
  country: 'UK',
  course: 'BA Arts',
  status: 'Reviewing',
  date: '2023-10-08'
}, {
  id: 7,
  name: 'James Taylor',
  email: 'james@example.com',
  country: 'Canada',
  course: 'MSc Bio',
  status: 'Approved',
  date: '2023-10-07'
}, {
  id: 8,
  name: 'Robert Martin',
  email: 'robert@example.com',
  country: 'USA',
  course: 'PhD Physics',
  status: 'Pending',
  date: '2023-10-05'
}];
export function AdminDashboardPage() {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const filteredApps = applications.filter(app => {
    const matchesSearch = app.name.toLowerCase().includes(searchTerm.toLowerCase()) || app.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'All' || app.status === statusFilter;
    return matchesSearch && matchesStatus;
  });
  const getStatusVariant = (status: string) => {
    switch (status) {
      case 'Approved':
        return 'success';
      case 'Rejected':
        return 'danger';
      case 'Reviewing':
        return 'warning';
      default:
        return 'neutral';
    }
  };
  return <div className="min-h-screen bg-[#faf8f5] flex" data-id="element-288">
      {/* Sidebar */}
      <aside className="w-64 bg-[#1e3a5f] text-white hidden md:flex flex-col fixed h-full" data-id="element-289">
        <div className="p-6 flex items-center space-x-2 border-b border-white/10" data-id="element-290">
          <GraduationCap className="h-6 w-6 text-[#f59e0b]" data-id="element-291" />
          <span className="text-xl font-bold" data-id="element-292">Study Board</span>
        </div>

        <nav className="flex-1 p-4 space-y-2" data-id="element-293">
          <a href="#" className="flex items-center space-x-3 px-4 py-3 bg-white/10 rounded-lg text-white" data-id="element-294">
            <LayoutDashboard className="h-5 w-5" data-id="element-295" />
            <span data-id="element-296">Dashboard</span>
          </a>
          <a href="#" className="flex items-center space-x-3 px-4 py-3 text-gray-300 hover:bg-white/5 rounded-lg transition-colors" data-id="element-297">
            <Users className="h-5 w-5" data-id="element-298" />
            <span data-id="element-299">Students</span>
          </a>
          <a href="#" className="flex items-center space-x-3 px-4 py-3 text-gray-300 hover:bg-white/5 rounded-lg transition-colors" data-id="element-300">
            <FileText className="h-5 w-5" data-id="element-301" />
            <span data-id="element-302">Applications</span>
          </a>
          <a href="#" className="flex items-center space-x-3 px-4 py-3 text-gray-300 hover:bg-white/5 rounded-lg transition-colors" data-id="element-303">
            <Settings className="h-5 w-5" data-id="element-304" />
            <span data-id="element-305">Settings</span>
          </a>
        </nav>

        <div className="p-4 border-t border-white/10" data-id="element-306">
          <Link to="/" className="flex items-center space-x-3 px-4 py-3 text-gray-300 hover:text-white transition-colors" data-id="element-307">
            <LogOut className="h-5 w-5" data-id="element-308" />
            <span data-id="element-309">Logout</span>
          </Link>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 md:ml-64 p-8" data-id="element-310">
        <div className="flex justify-between items-center mb-8" data-id="element-311">
          <h1 className="text-2xl font-bold text-[#1e3a5f]" data-id="element-312">
            Dashboard Overview
          </h1>
          <div className="flex items-center space-x-4" data-id="element-313">
            <div className="text-right" data-id="element-314">
              <p className="text-sm font-medium text-gray-900" data-id="element-315">Admin User</p>
              <p className="text-xs text-gray-500" data-id="element-316">Super Admin</p>
            </div>
            <div className="h-10 w-10 bg-gray-200 rounded-full" data-id="element-317"></div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8" data-id="element-318">
          <Card className="p-6" data-id="element-319">
            <p className="text-sm text-gray-500 mb-1" data-id="element-320">Total Applications</p>
            <p className="text-3xl font-bold text-[#1e3a5f]" data-id="element-321">1,248</p>
          </Card>
          <Card className="p-6" data-id="element-322">
            <p className="text-sm text-gray-500 mb-1" data-id="element-323">Pending Review</p>
            <p className="text-3xl font-bold text-amber-600" data-id="element-324">45</p>
          </Card>
          <Card className="p-6" data-id="element-325">
            <p className="text-sm text-gray-500 mb-1" data-id="element-326">Approved</p>
            <p className="text-3xl font-bold text-green-600" data-id="element-327">892</p>
          </Card>
          <Card className="p-6" data-id="element-328">
            <p className="text-sm text-gray-500 mb-1" data-id="element-329">Rejected</p>
            <p className="text-3xl font-bold text-red-600" data-id="element-330">311</p>
          </Card>
        </div>

        {/* Applications Table */}
        <Card className="overflow-hidden" data-id="element-331">
          <div className="p-6 border-b border-gray-100 flex flex-col md:flex-row justify-between items-center gap-4" data-id="element-332">
            <h2 className="text-lg font-bold text-[#1e3a5f]" data-id="element-333">
              Recent Applications
            </h2>
            <div className="flex space-x-4 w-full md:w-auto" data-id="element-334">
              <div className="relative flex-1 md:w-64" data-id="element-335">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" data-id="element-336" />
                <input type="text" placeholder="Search students..." className="w-full pl-10 pr-4 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-id="element-337" />
              </div>
              <Button variant="outline" size="sm" data-id="element-338">
                <Download className="h-4 w-4 mr-2" data-id="element-339" /> Export
              </Button>
            </div>
          </div>

          <div className="overflow-x-auto" data-id="element-340">
            <table className="w-full text-sm text-left" data-id="element-341">
              <thead className="bg-gray-50 text-gray-600 font-medium" data-id="element-342">
                <tr data-id="element-343">
                  <th className="px-6 py-4" data-id="element-344">Student Name</th>
                  <th className="px-6 py-4" data-id="element-345">Country</th>
                  <th className="px-6 py-4" data-id="element-346">Course</th>
                  <th className="px-6 py-4" data-id="element-347">Date</th>
                  <th className="px-6 py-4" data-id="element-348">Status</th>
                  <th className="px-6 py-4 text-right" data-id="element-349">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100" data-id="element-350">
                {filteredApps.map(app => <tr key={app.id} className="hover:bg-gray-50 transition-colors" data-id="element-351">
                    <td className="px-6 py-4" data-id="element-352">
                      <div data-id="element-353">
                        <p className="font-medium text-[#1e3a5f]" data-id="element-354">{app.name}</p>
                        <p className="text-xs text-gray-500" data-id="element-355">{app.email}</p>
                      </div>
                    </td>
                    <td className="px-6 py-4" data-id="element-356">{app.country}</td>
                    <td className="px-6 py-4" data-id="element-357">{app.course}</td>
                    <td className="px-6 py-4 text-gray-500" data-id="element-358">{app.date}</td>
                    <td className="px-6 py-4" data-id="element-359">
                      <Badge variant={getStatusVariant(app.status) as any} data-id="element-360">
                        {app.status}
                      </Badge>
                    </td>
                    <td className="px-6 py-4 text-right" data-id="element-361">
                      <button className="text-gray-400 hover:text-[#1e3a5f]" data-id="element-362">
                        <MoreVertical className="h-5 w-5" data-id="element-363" />
                      </button>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>
          <div className="p-4 border-t border-gray-100 text-center text-sm text-gray-500" data-id="element-364">
            Showing {filteredApps.length} of {applications.length} applications
          </div>
        </Card>
      </main>
    </div>;
}